import { PolicyAppState, SectionState, SectionStatus } from '../types';
import { ModuleId, SectionId, TreePath } from '@nirvana/shared/dtos/types/policies/common';
import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { PoliciesReduxActions } from '../actions';

// SELECTORS
export const getFullUpdateStatusNeeded = (state: PolicyAppState): boolean =>
  state.accordionsSlice.status.fullUpdateNeeded ?? false;
export const getPathUpdateNeeded = (state: PolicyAppState): boolean =>
  state.accordionsSlice.status.pathUpdateNeeded ?? false;
export const getPathToBeUpdatedWithStatus = (state: PolicyAppState): TreePath | undefined =>
  state.accordionsSlice.status.pathToBeUpdated;

export const getSectionStatus = (state: PolicyAppState, path: TreePath): SectionStatus => {
  return state.modules[path.moduleId]?.sections[path.sectionId].status;
};
export const getSectionStatuses = (state: PolicyAppState, moduleId: ModuleId): SectionStatus[] => {
  const sectionsStatus: SectionStatus[] = [];
  const module = state.modules[moduleId];

  if (!module) {
    return [];
  }

  Object.entries(module.sections).forEach(([_, sectionState]: [SectionId, SectionState]) => {
    sectionsStatus.push(sectionState.status);
  });

  return sectionsStatus;
};

// ACTIONS
export const setIsStatusPathUpdateNeeded = createAction<boolean>(PoliciesReduxActions.SET_IS_STATUS_PATH_UPDATE_NEEDED);
export const setIsStatusPathUpdateNeededActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<boolean>
): void => {
  state.accordionsSlice.status.pathUpdateNeeded = action.payload;

  if (!action.payload) {
    state.accordionsSlice.status.pathToBeUpdated = undefined;
  }
};

export const setIsStatusFullUpdateNeeded = createAction<boolean>(PoliciesReduxActions.SET_IS_STATUS_FULL_UPDATE_NEEDED);
export const setIsStatusFullUpdateNeededActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<boolean>
): void => {
  state.accordionsSlice.status.fullUpdateNeeded = action.payload;
};

export const setPathToBeUpdatedWithStatus = createAction<{
  path: TreePath;
  status: SectionStatus;
}>(PoliciesReduxActions.SET_PATH_TO_BE_UPDATED_WITH_STATUS);
export const setPathToBeUpdatedWithStatusActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ path: TreePath; status: SectionStatus }>
): void => {
  const moduleId = action.payload.path.moduleId;
  const sectionId = action.payload.path.sectionId;
  const module = state.modules[moduleId];

  if (!module) {
    return;
  }

  module.sections[sectionId].status = action.payload.status;
  state.accordionsSlice.status.pathUpdateNeeded = true;
  state.accordionsSlice.status.pathToBeUpdated = action.payload.path;
};

export const setPathStatuses = createAction<
  {
    path: TreePath;
    status: SectionStatus;
  }[]
>(PoliciesReduxActions.SET_PATH_STATUSES);
export const setPathStatusesActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ path: TreePath; status: SectionStatus }[]>
): void => {
  action.payload.forEach(({ path, status }) => {
    const module = state.modules[path.moduleId];

    if (!module) {
      return;
    }

    module.sections[path.sectionId].status = status;
  });
};
